// Account Detail Page
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { crmAPI, userAPI, saleOpportunityAPI, associationAPI, hospitalityAPI, activityAPI, marketingAPI } from '../../services/api';
import api from '../../services/api';
import OfflineConflictBadge from '../../components/common/OfflineConflictBadge';

import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Building2, User, Camera, X, Upload, ChevronDown, Archive, ArchiveRestore, Gavel, ChevronRight, Heart, FileText, Search, TrendingUp, Eye, EyeOff, UserX, Mail, RotateCw, Loader2 as Loader2Icon, Bell, BellOff, CircleStar } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import ContactEntries from '../../components/crm/ContactEntries';
import AssociationsList from '../../components/crm/AssociationsList';
import FileUpload from '../../components/common/FileUpload';
import CameraCaptureDialog from '../../components/shared/CameraCaptureDialog';
import AccountOverview from '../../components/crm/AccountOverview';
import GoalsTab from '../../components/crm/GoalsTab';
import FinancialsTab from '../../components/crm/FinancialsTab';
import HorsesTab from '../../components/crm/HorsesTab';
import SyndicateOwnershipTab from '../../components/syndicates/SyndicateOwnershipTab';
import { useConfirm } from '../../components/common/ConfirmDialog';


const stageBadges = {
  presale_create: { label: 'Potential Sale Entry', className: 'bg-gray-100 text-gray-700' },
  presale_confirm: { label: 'Confirmed Sale Entry', className: 'bg-blue-100 text-blue-700' },
  presale_deliverables: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_ready: { label: 'Ready for Sale', className: 'bg-green-100 text-green-700' },
  live_sale: { label: 'Live Sale', className: 'bg-primary text-white' },
  post_sale: { label: 'Complete', className: 'bg-muted text-muted-foreground' },
};

const resultBadges = {
  sold: { label: 'SOLD', className: 'bg-green-600 text-white' },
  rna: { label: 'RNA', className: 'bg-yellow-600 text-white' },
  withdrawn: { label: 'Withdrawn', className: 'bg-red-100 text-red-700' },
};

/* Syndicates membership tab for regular accounts */
const SyndicateMembershipsTab = ({ accountId }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data: records = [], isLoading: loading } = useQuery({
    queryKey: QK.syndicateOwnershipByOwner(accountId),
    queryFn: () => api.get(`/syndicate-ownership/by-owner/${accountId}`).then(r => r.data || []),
    enabled: !!accountId,
  });

  const fmt = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const current = records.filter(r => r.is_current !== false);
  const historical = records.filter(r => r.is_current === false);

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2Icon className="w-6 h-6 animate-spin text-primary" /></div>
  );

  if (records.length === 0) return (
    <Card>
      <CardContent className="py-16 text-center">
        <CircleStar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
        <p className="text-muted-foreground">Not a member of any managed syndicates</p>
      </CardContent>
    </Card>
  );

  const MembershipTable = ({ rows, title }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-xs font-semibold text-muted-foreground uppercase">
                <th className="text-left px-4 py-2">Syndicate</th>
                <th className="text-left px-4 py-2">Ownership %</th>
                <th className="text-left px-4 py-2">Date Acquired</th>
                <th className="text-left px-4 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(rec => (
                <tr key={rec.id} className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                  onClick={() => navigate(`/syndicates/${rec.syndicate_crm_entity_id}`)}>
                  <td className="px-4 py-3 font-medium text-primary hover:underline">
                    {rec.syndicate_name || rec.syndicate_crm_entity_id}
                    {rec.interest_sold && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px] pointer-events-none">Interest sold</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">{rec.ownership_percent?.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(rec.date_acquired)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{rec.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {current.length > 0 && <MembershipTable rows={current} title="Current Syndicate Memberships" />}
      {historical.length > 0 && <MembershipTable rows={historical} title="Historical Syndicate Memberships" />}
    </div>
  );
};

const AccountDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, isViewOnly, hasFinancialAccess, canEdit: canEditGlobal, canEditCRM, isFarm, isSyndicator } = useAuth();
  const { id } = useParams();
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: QK.crmEntity(id),
    queryFn: async () => {
      // Phase 1: account is the root — everything depends on it
      const accRes = await crmAPI.get(id);
      const accData = accRes.data;
      const ownerIds = [...(accData.approved_owner_ids || []), ...(accData.pending_owner_ids || [])];

      // Phase 2: all independent calls in parallel — includes Overview pre-warm data
      // so AccountOverview renders immediately populated when its tab mounts.
      const [
        tenantRes, subRes,
        usersRes, salesRes, assocRes,
        entitiesRes, hospRes, purchasesRes,
        viewingsRes, ownersRes,
        ovTasksRes, ovCommsRes, ovMktRes, ovContactRes,
      ] = await Promise.allSettled([
        accData.tenant_id ? api.get(`/tenants/${accData.tenant_id}`) : null,
        accData.crm_class === 'account' ? api.get(`/tenant-alert-prefs/check/${accData.id}`) : null,
        userAPI.list(),
        saleOpportunityAPI.list(),
        associationAPI.list(id),
        crmAPI.list(),
        hospitalityAPI.list({ crm_entity_id: id }),
        api.get(`/sale-opportunities/purchases?crm_entity_id=${id}`),
        api.get(`/sale-cards/entity-viewings?crm_entity_id=${id}`),
        ownerIds.length > 0 ? api.get(`/system-users/owner-users?ids=${ownerIds.join(',')}`) : null,
        // Overview pre-warm
        activityAPI.list({ crm_entity_id: id, activity_type: 'task' }),
        activityAPI.list({ crm_entity_id: id, activity_type: 'communication' }),
        marketingAPI.list({ crm_entity_id: id }),
        api.get(`/contact-entries?crm_entity_id=${id}`),
      ]);

      const val = (r) => (r?.status === 'fulfilled' && r.value ? r.value : null);

      if (val(tenantRes)) accData.tenant_name = val(tenantRes).data?.name;
      const isAlertSubscribed = val(subRes)?.data?.subscribed ?? false;

      const usersList = val(usersRes)?.data || [];
      let managerName = 'Unknown', adminName = '-';
      if (accData.crm_class === 'account') {
        const manager = usersList.find(u => u.id === accData.account_manager_id);
        if (manager) managerName = manager.full_name || manager.name || `${manager.first_name} ${manager.last_name}`;
        const admin = usersList.find(u => u.id === accData.account_admin_id);
        adminName = admin ? (admin.full_name || admin.name || `${admin.first_name} ${admin.last_name}`) : '-';
      } else {
        managerName = '-'; adminName = '-';
      }

      // Phase 3: per-opp horse + sale enrichment (parallelised)
      const accountOpportunities = (val(salesRes)?.data || []).filter(s => s.owner_crm_entity_ids?.includes(id));
      const horseIds = [...new Set(accountOpportunities.map(o => o.horse_id).filter(Boolean))];
      const saleIds  = [...new Set(accountOpportunities.map(o => o.sale_id).filter(Boolean))];

      const [horsesResults, salesResults] = await Promise.all([
        Promise.allSettled(horseIds.map(hid => api.get(`/horses/${hid}`).then(r => [hid, r.data]))),
        Promise.allSettled(saleIds.map(sid => api.get(`/sales/${sid}`).then(r => [sid, r.data]))),
      ]);
      const horsesMap = Object.fromEntries(horsesResults.filter(r => r.status === 'fulfilled').map(r => r.value));
      const salesMap  = Object.fromEntries(salesResults.filter(r => r.status === 'fulfilled').map(r => r.value));

      const linkedOwners = val(ownersRes)
        ? (val(ownersRes).data || []).map(u => ({
            ...u,
            status: (accData.approved_owner_ids || []).includes(u.id) ? 'approved' : 'pending',
          }))
        : [];

      return {
        account: accData,
        isAlertSubscribed,
        managerName,
        adminName,
        saleOpportunities: accountOpportunities.map(opp => ({ ...opp, horse: horsesMap[opp.horse_id] })),
        sales: salesMap,
        associations: val(assocRes)?.data || [],
        entities: val(entitiesRes)?.data || [],
        hospitality: val(hospRes)?.data || [],
        purchases: val(purchasesRes)?.data || [],
        entityViewings: val(viewingsRes)?.data || [],
        users: usersList,
        linkedOwners,
        entityType: accData.crm_class,
        // Overview pre-warm payloads
        overviewTasks:         val(ovTasksRes)?.data   || [],
        overviewComms:         val(ovCommsRes)?.data   || [],
        overviewHospitality:   val(hospRes)?.data      || [],  // reuse already-fetched hospRes
        overviewMarketing:     val(ovMktRes)?.data     || [],
        overviewContactEntries: val(ovContactRes)?.data || [],
      };
    },
    enabled: !!id,
    onError: (error) => toast.error('Failed to load account details: ' + (error.message || 'Unknown error')),
  });

  // Destructure server state with defaults
  const account           = data?.account ?? null;
  const isAlertSubscribed = data?.isAlertSubscribed ?? false;
  const managerName       = data?.managerName ?? 'Unknown';
  const adminName         = data?.adminName ?? '-';
  const saleOpportunities = data?.saleOpportunities ?? [];
  const sales             = data?.sales ?? {};
  const associations      = data?.associations ?? [];
  const entities          = data?.entities ?? [];
  const hospitality       = data?.hospitality ?? [];
  const purchases         = data?.purchases ?? [];
  const entityViewings    = data?.entityViewings ?? [];
  const users             = data?.users ?? [];
  const linkedOwners      = data?.linkedOwners ?? [];
  const entityType        = data?.entityType ?? (location.state?.entityType || null);

  const invalidateAccount = () => qc.invalidateQueries({ queryKey: QK.crmEntity(id) });

  // Pre-warm AccountOverview cache — useLayoutEffect fires synchronously after DOM
  // mutations but BEFORE paint, so the cache is populated before AccountOverview renders.
  useLayoutEffect(() => {
    if (!data?.account?.id) return;
    const aid = data.account.id;
    if (data.overviewTasks)          qc.setQueryData(QK.activities({ crm_entity_id: aid, activity_type: 'task' }),          data.overviewTasks);
    if (data.overviewComms)          qc.setQueryData(QK.activities({ crm_entity_id: aid, activity_type: 'communication' }), data.overviewComms);
    if (data.overviewHospitality)    qc.setQueryData(QK.hospitality({ crm_entity_id: aid }),    data.overviewHospitality);
    if (data.overviewMarketing)      qc.setQueryData(QK.marketing({ crm_entity_id: aid }),      data.overviewMarketing);
    if (data.overviewContactEntries) qc.setQueryData(QK.contactEntries(aid),                    data.overviewContactEntries);
  }, [data?.account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ──────────────────────────────────────────────────────────────
  const [togglingAlert, setTogglingAlert] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [purchasesSearch, setPurchasesSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [oppsSearch, setOppsSearch] = useState('');
  const [purchasesOpen, setPurchasesOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [oppsOpen, setOppsOpen] = useState(true);
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [linkedOwnersLoading, setLinkedOwnersLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhotoDataUrl, setProfilePhotoDataUrl] = useState(null);
  const [savingRotation, setSavingRotation] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [convertingTo, setConvertingTo] = useState('');
  const [conversionForm, setConversionForm] = useState({
    account_manager_id: '',
    account_admin_id: '',
    account_active: true,
  });
  const [archiving, setArchiving] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('crm_entity_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Upload failed');
      }
      
      const fileData = await response.json();
      
      // Update account with new profile photo URL (relative path)
      await crmAPI.update(id, { 
        main_photo_url: `/api/files/public/${fileData.id}`
      });

      // Immediately update preview
      const previewRes = await fetch(`${window.location.origin}/api/files/public/${fileData.id}`);
      if (previewRes.ok) {
        const blob = await previewRes.blob();
        setProfilePhotoDataUrl(URL.createObjectURL(blob));
      }
      
      toast.success('Profile photo updated');
      await invalidateAccount();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCameraCapture = async (file) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('crm_entity_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Upload failed');
      }

      const fileData = await response.json();
      await crmAPI.update(id, {
        main_photo_url: `/api/files/public/${fileData.id}`
      });

      const previewRes = await fetch(`${window.location.origin}/api/files/public/${fileData.id}`);
      if (previewRes.ok) {
        const blob = await previewRes.blob();
        setProfilePhotoDataUrl(URL.createObjectURL(blob));
      }

      toast.success('Profile photo updated');
      await invalidateAccount();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRotateAndSave = async () => {
    const photoSrc = profilePhotoDataUrl ||
      (account?.main_photo_url
        ? (account.main_photo_url.startsWith('http') ? account.main_photo_url : `${window.location.origin}${account.main_photo_url}`)
        : null);
    if (!photoSrc) return;
    setSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = photoSrc; });
      const canvas = document.createElement('canvas');
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      const formData = new FormData();
      formData.append('file', blob, 'rotated-photo.jpg');
      formData.append('crm_entity_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/files/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const fileData = await response.json();
      await crmAPI.update(id, { main_photo_url: `/api/files/public/${fileData.id}` });
      setProfilePhotoDataUrl(URL.createObjectURL(blob));
      toast.success('Photo rotated and saved');
    } catch (e) {
      console.error('Failed to rotate photo:', e);
      toast.error('Failed to save rotated photo');
    } finally {
      setSavingRotation(false);
    }
  };


  const handleConversion = async () => {
    if (!convertingTo) {
      toast.error('Please select a conversion type');
      return;
    }

    try {
      const updateData = {
        crm_class: convertingTo,
        account_manager_id: conversionForm.account_manager_id || account.account_manager_id,
        account_admin_id: conversionForm.account_admin_id || account.account_admin_id,
        account_active: conversionForm.account_active,
      };

      await crmAPI.update(id, updateData);
      toast.success(`Successfully converted to ${convertingTo}`);
      setConversionDialogOpen(false);
      await invalidateAccount();
    } catch (error) {
      console.error('Failed to convert:', error);
      toast.error('Failed to convert account');
    }
  };

  const handleArchive = async () => {
    try {
      setArchiving(true);
      const newArchivedState = !account.archived;
      
      await crmAPI.update(id, { archived: newArchivedState });
      
      toast.success(newArchivedState ? 'Account archived' : 'Account restored');
      await invalidateAccount();
    } catch (error) {
      console.error('Failed to archive/restore:', error);
      toast.error('Failed to update archive status');
    } finally {
      setArchiving(false);
    }
  };


  // Check if current user can edit the profile photo
  // For contacts, any logged-in tenant user can edit
  const canEditPhoto = currentUser && account && !isViewOnly() && (
    account.crm_class === 'contact' ||
    currentUser.id === account.account_manager_id ||
    currentUser.id === account.account_admin_id ||
    currentUser.roles?.includes('system_owner') ||
    currentUser.roles?.includes('tenant_system_admin')
  );
  
  // Check if current user can edit this account's data
  // View-only users cannot edit anything
  const canEditAccount = currentUser && account && !isViewOnly() && !(currentUser.roles?.includes('tenant_team_member') && account.crm_class === 'account') && (
    account.crm_class === 'contact' ||
    currentUser.id === account.account_manager_id ||
    currentUser.id === account.account_admin_id ||
    currentUser.roles?.includes('system_owner') ||
    currentUser.roles?.includes('tenant_system_admin')
  );

  if (isLoading) {
    return (
      <SidebarLayout activeOverride={entityType === 'contact' ? '/crm/contacts' : null}>
        <div className="space-y-4 animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center gap-4 pb-4 border-b">
            <Skeleton className="h-20 w-20 rounded-full shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-9 w-28" />
          </div>
          {/* Tab bar skeleton */}
          <div className="flex gap-2 border-b pb-2">
            {[80, 64, 72, 56, 80, 64].map((w, i) => (
              <Skeleton key={i} className={`h-8 w-${w === 80 ? 20 : w === 72 ? 18 : w === 64 ? 16 : 14}`} />
            ))}
          </div>
          {/* Content area skeleton — two-column layout matching overview */}
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div className="space-y-4">
              <Skeleton className="h-40 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-56 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-40 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!account) {
    return (
      <SidebarLayout activeOverride={entityType === 'contact' ? '/crm/contacts' : null}>
        <div className="p-8">
          <p className="text-muted-foreground">Account not found or still loading...</p>
        </div>
      </SidebarLayout>
    );
  }

  // Determine sidebar active override based on entity type
  const sidebarActiveOverride = account.is_tenant_managed ? '/syndicates'
    : account.crm_class === 'contact' ? '/crm/contacts' : null;

  const displayName = account.crm_type === 'organization' 
    ? (account.organization_name || account.system_name)
    : (account.person_full_name || `${account.person_first_name || ''} ${account.person_last_name || ''}`.trim() || account.system_name);

  const rating = account.account_rating || 'N/A';
  const role = account.organization_industry_role || account.person_industry_role || '-';

  return (
    <SidebarLayout activeOverride={sidebarActiveOverride}>
      <div className="space-y-6">
        {/* Header */}
        <Card className="bg-white border-primary shadow-sm">
          <CardContent className="p-3 sm:p-6">

            {/* Avatar + Name + Dropdown — stacked on mobile, one row on desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
              {/* Dropdown only: top on mobile, right side on desktop */}
              <div className="flex items-center justify-end gap-2 sm:order-last sm:ml-auto shrink-0 mb-1 sm:mb-0">
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs uppercase tracking-wide border-primary text-primary gap-1">
                    {account.crm_class === 'account' ? 'Account' : 'Contact'}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEditAccount ? (
                    <>
                      {!account.is_tenant_managed && (
                        <>
                          <DropdownMenuItem onClick={async () => {
                            setConvertingTo(account.crm_class === 'account' ? 'contact' : 'account');
                            setConversionForm({
                              account_manager_id: '',
                              account_admin_id: '',
                              account_active: true,
                            });
                            setConversionDialogOpen(true);
                          }}>
                            Convert to {account.crm_class === 'account' ? 'Contact' : 'Account'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={handleArchive} disabled={archiving}>
                        {account.archived ? (
                          <><ArchiveRestore className="w-4 h-4 mr-2" /> Unarchive</>
                        ) : (
                          <><Archive className="w-4 h-4 mr-2" /> Archive</>
                        )}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem disabled>
                      No actions available
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

              {/* Avatar + Name — inside the same flex row */}
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-10 h-10 sm:w-24 sm:h-24 bg-muted rounded-full flex items-center justify-center border-2 border-border shrink-0 relative group cursor-pointer"
                  onClick={() => setProfilePhotoPreview(true)}
                >
                  {(profilePhotoDataUrl || account?.main_photo_url) ? (
                    <img
                      src={profilePhotoDataUrl || (account.main_photo_url.startsWith('http') ? account.main_photo_url : `${window.location.origin}${account.main_photo_url}`)}
                      alt={displayName} className="w-full h-full rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <>
                      {account.crm_type === 'organization' ? (
                        <Building2 className="w-5 h-5 sm:w-10 sm:h-10 text-muted-foreground" />
                      ) : (
                        <User className="w-5 h-5 sm:w-10 sm:h-10 text-muted-foreground" />
                      )}
                    </>
                  )}
                  {canEditPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <h1 className="font-serif text-xl sm:text-3xl font-bold text-foreground leading-tight">
                    {displayName}
                  </h1>
                  <OfflineConflictBadge record={account} entityType="crm-entities" onDismissed={() => window.location.reload()} />
                  {account.archived && (
                    <Badge variant="outline" className="border-orange-400 text-orange-600 font-semibold text-xs shrink-0">
                      Archived
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Details row — inline bell at the far right */}
            <div className="flex items-end justify-between gap-4 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-1 flex-1">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Role</p>
                  <p className="font-medium capitalize">{role.replace(/_/g, ' ')}</p>
                </div>
                {!account.is_tenant_managed && (
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wider">Rating</p>
                    <p className="font-medium">{rating}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Manager</p>
                  <p className="font-medium">{managerName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Admin</p>
                  <p className="font-medium">{adminName}</p>
                </div>
              </div>
              {account.crm_class === 'account' && (
                <button
                  disabled={togglingAlert}
                  title={isAlertSubscribed ? 'Unsubscribe from sale alerts' : 'Subscribe to sale alerts for this account'}
                  className={`p-1 rounded shrink-0 transition-colors ${isAlertSubscribed ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={async () => {
                    setTogglingAlert(true);
                    try {
                      const r = await api.post(`/tenant-alert-prefs/toggle-account/${account.id}`);
                      qc.setQueryData(QK.crmEntity(id), prev => prev ? { ...prev, isAlertSubscribed: r.data.subscribed } : prev);
                      toast.success(r.data.subscribed ? 'Alert subscription enabled' : 'Alert subscription removed');
                    } catch { toast.error('Failed to update alert subscription'); }
                    finally { setTogglingAlert(false); }
                  }}
                  data-testid="account-alert-bell"
                >
                  {isAlertSubscribed
                    ? <Bell className="w-5 h-5 fill-primary" />
                    : <Bell className="w-5 h-5" />}
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent gap-6 overflow-x-auto">
            {['Overview',
              ...(account.is_tenant_managed ? ['Ownership'] : []),
              'Contact Details', 'All Associations', 'Goals', 'Horses', 'Sales History', 
              ...(account.crm_class === 'account' ? [
                ...(hasFinancialAccess() ? ['Financials'] : []),
                ...(!account.is_tenant_managed ? [
                  'Account Viewers',
                  ...(isSyndicator ? ['Syndicates'] : []),
                ] : []),
              ] : [])
            ].map(tab => (
              <TabsTrigger 
                key={tab} 
                value={tab.toLowerCase().replace(/\s+/g, '-')}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 whitespace-nowrap"
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <AccountOverview account={account} loading={isLoading} onAccountUpdate={invalidateAccount} canEditAccount={canEditAccount} />
          </TabsContent>

          {account.is_tenant_managed && (
            <TabsContent value="ownership">
              <SyndicateOwnershipTab syndicateId={id} canEdit={canEditAccount} />
            </TabsContent>
          )}

          <TabsContent value="contact-details">
            <ContactEntries 
              crmEntityId={id} 
              tenantId={account.tenant_id}
              readOnly={!canEditAccount}
            />
          </TabsContent>

          <TabsContent value="all-associations">
            <AssociationsList
              entityId={id}
              tenantId={account.tenant_id}
              canEditAccount={canEditAccount}
            />
          </TabsContent>

          <TabsContent value="goals">
            <GoalsTab 
              account={account} 
              onUpdate={invalidateAccount} 
              associations={associations}
              entities={entities}
              canEditAccount={canEditAccount}
            />
          </TabsContent>

          <TabsContent value="horses">
            <HorsesTab account={account} />
          </TabsContent>

          <TabsContent value="sales-history">
            <div className="space-y-6">
              {/* Sales Insights Card - only shown if there's data */}
              {(purchases.length > 0 || entityViewings.length > 0) && (() => {
                // Buying patterns
                const totalPurchases = purchases.length;
                const totalSpent = purchases.reduce((sum, p) => sum + (p.sale_price || 0), 0);
                const avgPrice = totalPurchases > 0 ? totalSpent / totalPurchases : 0;
                const saleCompanies = {};
                purchases.forEach(p => { if (p.sale_company) saleCompanies[p.sale_company] = (saleCompanies[p.sale_company] || 0) + 1; });
                const topCompany = Object.entries(saleCompanies).sort((a, b) => b[1] - a[1])[0];

                // Viewing patterns
                const totalCards = entityViewings.filter(c => (c.viewings || []).length > 0).length;
                const totalViewings = entityViewings.reduce((sum, c) => sum + (c.viewings || []).length, 0);
                const completedViewings = entityViewings.reduce((sum, c) => sum + (c.viewings || []).filter(v => v.status === 'completed').length, 0);
                const likedHorses = new Set();
                entityViewings.forEach(c => (c.viewings || []).forEach(v => { if (v.liked) likedHorses.add(v.horse_name); }));
                const viewToPurchaseRate = totalViewings > 0 && totalPurchases > 0 ? ((totalPurchases / totalViewings) * 100).toFixed(0) : null;

                // Vetting patterns
                const totalVetting = entityViewings.reduce((sum, c) => sum + (c.vetting_procedures || []).length, 0);
                // Average vets per purchased opportunity
                let avgVetsPerPurchase = null;
                if (purchases.length > 0 && totalVetting > 0) {
                  // Count vetting procedures that relate to purchased opportunities
                  let totalVetsForPurchases = 0;
                  let purchasesWithVets = 0;
                  purchases.forEach(p => {
                    const vetsForThis = entityViewings.reduce((sum, c) => {
                      return sum + (c.vetting_procedures || []).filter(vp => 
                        vp.horse_name === p.horse_name || vp.hip_number === p.hip_number
                      ).length;
                    }, 0);
                    totalVetsForPurchases += vetsForThis;
                    if (vetsForThis > 0) purchasesWithVets++;
                  });
                  if (purchasesWithVets > 0) {
                    avgVetsPerPurchase = (totalVetsForPurchases / purchases.length).toFixed(1);
                  }
                }

                // Sale engagement
                const saleEngagement = {};
                entityViewings.forEach(c => {
                  const key = c.sale_name || 'Unknown';
                  if (!saleEngagement[key]) saleEngagement[key] = { cards: 0, viewings: 0 };
                  if ((c.viewings || []).length > 0) saleEngagement[key].cards++;
                  saleEngagement[key].viewings += (c.viewings || []).length;
                });
                const mostActiveSale = Object.entries(saleEngagement).sort((a, b) => b[1].viewings - a[1].viewings)[0];
                const avgCardsPerSale = Object.keys(saleEngagement).length > 0 ? (totalCards / Object.keys(saleEngagement).length).toFixed(1) : 0;

                const hasInsights = totalPurchases > 0 || totalCards > 0 || totalVetting > 0;
                if (!hasInsights) return null;

                return (
                  <Card className="border-2 border-border shadow-sm">
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        <CardTitle className="text-sm font-semibold">Sales Insights</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {/* Buying Patterns */}
                        {totalPurchases > 0 && (
                          <div className="space-y-2 p-4 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Buying Patterns</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Purchases</span>
                                <span className="font-medium">{totalPurchases}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Spent</span>
                                <span className="font-medium">${totalSpent.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Purchase Price</span>
                                <span className="font-medium">${Math.round(avgPrice).toLocaleString()}</span>
                              </div>
                              {topCompany && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Preferred Sale Company</span>
                                  <span className="font-medium">{topCompany[0]} ({topCompany[1]})</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Viewing Patterns */}
                        {totalCards > 0 && (
                          <div className="space-y-2 p-4 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Viewing Patterns</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Cards</span>
                                <span className="font-medium">{totalCards}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Horses Viewed</span>
                                <span className="font-medium">{completedViewings} of {totalViewings} completed</span>
                              </div>
                              {likedHorses.size > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Liked Horses</span>
                                  <span className="font-medium">{[...likedHorses].join(', ')}</span>
                                </div>
                              )}
                              {viewToPurchaseRate && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">View-to-Purchase Rate</span>
                                  <span className="font-medium">{viewToPurchaseRate}%</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Vetting Patterns */}
                        {totalVetting > 0 && (
                          <div className="space-y-2 p-4 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vetting Patterns</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Vetting Requests</span>
                                <span className="font-medium">{totalVetting}</span>
                              </div>
                              {avgVetsPerPurchase && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Avg Vets per Purchase</span>
                                  <span className="font-medium">{avgVetsPerPurchase}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Sale Engagement */}
                        {Object.keys(saleEngagement).length > 0 && (
                          <div className="space-y-2 p-4 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sale Engagement</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Sales Attended</span>
                                <span className="font-medium">{Object.keys(saleEngagement).length}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Cards per Sale</span>
                                <span className="font-medium">{avgCardsPerSale}</span>
                              </div>
                              {mostActiveSale && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Most Active Sale</span>
                                  <span className="font-medium">{mostActiveSale[0]} ({mostActiveSale[1].viewings} viewings)</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Purchases Card */}
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setPurchasesOpen(!purchasesOpen)}>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${purchasesOpen ? '' : '-rotate-90'}`} />
                    <CardTitle>Purchases</CardTitle>
                  </div>
                  {purchasesOpen && (
                    <div className="relative max-w-xs mt-2" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search purchases..." value={purchasesSearch} onChange={(e) => setPurchasesSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                    </div>
                  )}
                </CardHeader>
                {purchasesOpen && (
                <CardContent className="px-4 py-0 pb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sale</TableHead>
                        <TableHead>Horse</TableHead>
                        <TableHead>Owners</TableHead>
                        <TableHead>Sale Price</TableHead>
                        <TableHead>Date Purchased</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const q = purchasesSearch.toLowerCase();
                        const filtered = purchases.filter(p => !q || [p.sale_name, p.horse_name, p.owners, p.sale_price?.toString(), p.date].some(v => (v || '').toLowerCase().includes(q)));
                        return filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8">
                            <p className="text-muted-foreground text-sm">No purchases found</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filtered.map((p) => (
                          <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/sales/opportunities/${p.id}`)}>
                            <TableCell className="text-sm">{p.sale_name || '-'}</TableCell>
                            <TableCell className="text-sm font-medium">{p.horse_name || '-'}</TableCell>
                            <TableCell className="text-sm">{p.owners || '-'}</TableCell>
                            <TableCell className="text-sm">{p.sale_price ? `$${p.sale_price.toLocaleString()}` : '-'}</TableCell>
                            <TableCell className="text-sm">{p.date ? new Date(p.date).toLocaleDateString() : '-'}</TableCell>
                          </TableRow>
                        ))
                      );
                      })()}
                    </TableBody>
                  </Table>
                </CardContent>
                )}
              </Card>
              {/* Sales History / Viewings by Card */}
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setHistoryOpen(!historyOpen)}>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${historyOpen ? '' : '-rotate-90'}`} />
                    <CardTitle>Sales History</CardTitle>
                  </div>
                  {historyOpen && (
                    <div className="relative max-w-xs mt-2" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search history..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} className="pl-9 h-8 text-sm" />
                    </div>
                  )}
                </CardHeader>
                {historyOpen && (
                <CardContent className="px-4 py-0 pb-4">
                  {(() => {
                    const q = historySearch.toLowerCase();
                    const filtered = entityViewings.filter(c => !q || [c.sale_name, c.sale_company, c.sale_year, c.date].some(v => (v || '').toLowerCase().includes(q)));
                    return filtered.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground text-sm">No viewing or vetting recorded</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Sale</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Interaction Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((card) => (
                          <React.Fragment key={card.card_id}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => setExpandedCard(expandedCard === card.card_id ? null : card.card_id)}
                            >
                              <TableCell className="w-8 px-2">
                                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedCard === card.card_id ? 'rotate-90' : ''}`} />
                              </TableCell>
                              <TableCell className="text-sm">
                                <div>
                                  <p className="font-medium">{card.sale_name || '-'}</p>
                                  {(card.sale_company || card.sale_year) && (
                                    <p className="text-xs text-muted-foreground">
                                      {[card.sale_company, card.sale_year].filter(Boolean).join(' • ')}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">{card.date ? new Date(card.date + 'T00:00:00').toLocaleDateString() : '-'}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {(card.viewings || []).length > 0 && (
                                    <Badge variant="outline" className="text-xs">Card</Badge>
                                  )}
                                  {(card.vetting_procedures || []).length > 0 && (
                                    <Badge variant="outline" className="text-xs">Vetting</Badge>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            {expandedCard === card.card_id && (
                              <TableRow>
                                <TableCell colSpan={4} className="p-0 bg-muted/20">
                                  <div className="px-6 py-3 space-y-3">
                                    {/* Card viewings in outlined box */}
                                    {(card.viewings || []).length > 0 && (
                                      <div className="border rounded p-3 bg-white">
                                        <div className="flex items-center gap-2 mb-2 text-xs">
                                          <span className="font-semibold">Card</span>
                                          <span className="text-muted-foreground">{card.date ? new Date(card.date + 'T00:00:00').toLocaleDateString() : ''}</span>
                                          {card.card_type && card.card_type !== 'standard' && card.card_type !== 'vetting_only' && (
                                            <Badge variant="outline" className="text-xs py-0 capitalize">{card.card_type.replace(/_/g, ' ')}</Badge>
                                          )}
                                          {card.card_status && (
                                            <Badge className={`text-xs py-0 capitalize ${
                                              card.card_status === 'completed' || card.card_status === 'complete' ? 'bg-green-100 text-green-700' :
                                              card.card_status === 'requeued' ? 'bg-gray-100 text-gray-700' :
                                              'bg-yellow-100 text-yellow-700'
                                            }`}>
                                              {card.card_status === 'completed' || card.card_status === 'complete' ? 'Complete' : card.card_status?.replace(/_/g, ' ') || 'Pending'}
                                            </Badge>
                                          )}
                                        </div>
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead className="w-[80px]">Hip #</TableHead>
                                              <TableHead>Horse</TableHead>
                                              <TableHead>Status</TableHead>
                                              <TableHead>Notes</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {card.viewings.map((v) => (
                                              <TableRow key={v.id}>
                                                <TableCell className="text-sm">#{v.hip_number || '-'}</TableCell>
                                                <TableCell className="text-sm font-medium">{v.horse_name || '-'}</TableCell>
                                                <TableCell className="text-sm">
                                                  <div className="flex items-center gap-2">
                                                    <Badge className={`text-xs ${
                                                      v.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                      v.status === 'skipped' ? 'bg-red-100 text-red-700' :
                                                      'bg-gray-100 text-gray-700'
                                                    }`}>
                                                      {v.status === 'completed' ? 'Done' : v.status === 'skipped' ? 'Skipped' : 'Pending'}
                                                    </Badge>
                                                    {v.status === 'completed' && v.updated_at && (
                                                      <span className="text-xs text-muted-foreground">
                                                        ({new Date(v.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(v.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })})
                                                      </span>
                                                    )}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                  <div className="flex items-center gap-2">
                                                    {v.liked && <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" />}
                                                    {v.viewed_vet_report && <FileText className="w-3.5 h-3.5 text-blue-500" />}
                                                    {v.notes ? <span className="text-xs">{v.notes}</span> : (!v.liked && !v.viewed_vet_report ? <span className="text-muted-foreground">-</span> : null)}
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    )}
                                    {/* Vetting Procedures in outlined box */}
                                    {(card.vetting_procedures || []).length > 0 && (
                                      <div className="border rounded p-3 bg-white">
                                        <div className="flex items-center gap-2 mb-2 text-xs">
                                          <span className="font-semibold">Vetting Procedures</span>
                                          <Badge variant="outline" className="text-xs py-0">{card.vetting_procedures.length}</Badge>
                                        </div>
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead className="w-[80px]">Hip #</TableHead>
                                              <TableHead>Horse</TableHead>
                                              <TableHead>Vet</TableHead>
                                              <TableHead>Client</TableHead>
                                              <TableHead>Procedure</TableHead>
                                              <TableHead>Date</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {card.vetting_procedures.map((vp) => (
                                              <TableRow key={vp.id}>
                                                <TableCell className="text-sm">#{vp.hip_number || '-'}</TableCell>
                                                <TableCell className="text-sm">{vp.horse_name || '-'}</TableCell>
                                                <TableCell className="text-sm">{vp.vet_name || '-'}</TableCell>
                                                <TableCell className="text-sm">{vp.client_name || '-'}</TableCell>
                                                <TableCell className="text-sm capitalize">
                                                  {vp.procedure?.replace(/_/g, ' ') || '-'}
                                                  {vp.viewed_vet_report && <FileText className="w-3.5 h-3.5 text-blue-500 inline ml-1" />}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                  {vp.procedure_date_time ? new Date(vp.procedure_date_time).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  );
                  })()}
                </CardContent>
                )}
              </Card>
              
              {/* Sale Opportunities Card */}
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setOppsOpen(!oppsOpen)}>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${oppsOpen ? '' : '-rotate-90'}`} />
                    <CardTitle>Sale Opportunities</CardTitle>
                  </div>
                  {oppsOpen && (
                    <div className="relative max-w-xs mt-2" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search opportunities..." value={oppsSearch} onChange={(e) => setOppsSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                    </div>
                  )}
                </CardHeader>
                {oppsOpen && (
                <CardContent className="px-4 py-0 pb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sale</TableHead>
                        <TableHead>Horse</TableHead>
                        <TableHead>Presale Stage</TableHead>
                        <TableHead>Hip #</TableHead>
                        <TableHead>Result</TableHead>
                        <TableHead>Sale Price</TableHead>
                        <TableHead>Date Created</TableHead>
                        <TableHead className="text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const q = oppsSearch.toLowerCase();
                        const filtered = saleOpportunities.filter(opp => {
                          if (!q) return true;
                          const sale = sales[opp.sale_id];
                          return [sale?.sale_name, sale?.sale_company, opp.horse?.system_name, opp.opportunity_name, opp.hip_number?.toString(), opp.result, opp.sale_price?.toString()].some(v => (v || '').toLowerCase().includes(q));
                        });
                        return filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-12">
                            <Gavel className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                            <p className="text-muted-foreground">No sale opportunities for this account</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filtered.map((opp) => {
                          const sale = sales[opp.sale_id];
                          const stageBadge = stageBadges[opp.stage];
                          const resultBadge = resultBadges[opp.result];

                          return (
                            <TableRow
                              key={opp.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                            >
                              <TableCell className="text-sm">
                                <div>
                                  <p className="font-medium">
                                    {sale?.sale_name || opp.opportunity_name?.split(' - ')[0] || '-'}
                                  </p>
                                  {sale && (
                                    <p className="text-xs text-muted-foreground">
                                      {sale.sale_company} • {new Date(sale.start_date || sale.sale_start_date).getFullYear()}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                <p className="font-medium">
                                  {opp.horse?.system_name || opp.opportunity_name?.split(' - ')[1] || '-'}
                                </p>
                              </TableCell>
                              <TableCell className="text-sm">
                                {stageBadge ? (
                                  <Badge className={stageBadge.className}>{stageBadge.label}</Badge>
                                ) : (
                                  <span>-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm">#{opp.hip_number || 'TBD'}</TableCell>
                              <TableCell className="text-sm">
                                {resultBadge ? (
                                  <Badge className={resultBadge.className}>{resultBadge.label}</Badge>
                                ) : (
                                  <span>-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm font-medium">
                                {opp.sale_price ? `$${opp.sale_price.toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell className="text-sm">
                                {opp.created_at ? new Date(opp.created_at).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      );
                      })()}
                    </TableBody>
                  </Table>
                </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="financials">
            <FinancialsTab 
              account={account} 
              onUpdate={invalidateAccount}
              hospitality={hospitality}
              users={users}
              entities={entities}
              canEditAccount={canEditAccount}
              isFarm={isFarm}
            />
          </TabsContent>

          <TabsContent value="account-viewers">
            <div className="space-y-6">
              {/* Access Key */}
              <Card>
                <CardHeader>
                  <CardTitle>Access Key</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">Share this access key with owners to grant them portal access to this account.</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-muted rounded px-4 py-2 font-mono text-sm tracking-wider">
                      {showAccessKey ? account.access_key : '••••••••••••••••'}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowAccessKey(!showAccessKey)}>
                      {showAccessKey ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {showAccessKey ? 'Hide' : 'Show'}
                    </Button>
                    {showAccessKey && (
                      <Button variant="outline" size="sm" onClick={async () => { navigator.clipboard.writeText(account.access_key); toast.success('Copied to clipboard'); }}>
                        Copy
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
                      <Mail className="w-4 h-4 mr-1" /> Share
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Linked Owner Users */}
              <Card>
                <CardHeader>
                  <CardTitle>Linked Owner Portal Users</CardTitle>
                </CardHeader>
                <CardContent className="px-4 py-0 pb-4">
                  {linkedOwners.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No owner portal users linked to this account.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Last Login</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {linkedOwners.map((owner) => (
                          <TableRow key={owner.id}>
                            <TableCell className="text-sm font-medium">{owner.full_name || `${owner.first_name || ''} ${owner.last_name || ''}`}</TableCell>
                            <TableCell className="text-sm">{owner.email}</TableCell>
                            <TableCell>
                              <Badge className={owner.status === 'approved' ? 'bg-green-100 text-green-700 pointer-events-none' : 'bg-yellow-100 text-yellow-700 pointer-events-none'}>
                                {owner.status === 'approved' ? 'Approved' : 'Pending'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{owner.last_login ? new Date(owner.last_login).toLocaleString() : 'Never'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                {owner.status === 'pending' && (
                                  <Button variant="outline" size="sm" className="h-7 text-xs text-green-700" onClick={async () => {
                                    try {
                                      await api.post('/owner-portal/approve-access', { entity_id: id, owner_user_id: owner.id });
                                      toast.success('Access approved');
                                      invalidateAccount();
                                    } catch { toast.error('Failed to approve'); }
                                  }}>
                                    Approve
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={async () => {
                                  if (!await confirm({ title: `Disconnect ${owner.full_name || owner.email} from this account?`, destructive: true })) return;
                                  try {
                                    await api.post('/owner-portal/reject-access', { entity_id: id, owner_user_id: owner.id });
                                    toast.success('Access disconnected');
                                    invalidateAccount();
                                  } catch { toast.error('Failed to disconnect'); }
                                }}>
                                  <UserX className="w-3 h-3 mr-1" /> Disconnect
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Syndicates Tab — only for regular accounts (not managed syndicates) */}
          {!account.is_tenant_managed && (
            <TabsContent value="syndicates">
              <SyndicateMembershipsTab accountId={id} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      
      {/* Profile Photo Preview/Upload Dialog */}
      <Dialog open={profilePhotoPreview} onOpenChange={setProfilePhotoPreview}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-2 flex-1 overflow-hidden">
            {(profilePhotoDataUrl || account?.main_photo_url) ? (
              <div className="flex items-center justify-center bg-muted/30 rounded-lg p-2 overflow-hidden">
                <img
                  src={profilePhotoDataUrl || (account.main_photo_url.startsWith('http') ? account.main_photo_url : `${window.location.origin}${account.main_photo_url}`)}
                  alt={displayName}
                  className="max-w-full max-h-[280px] object-contain rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center bg-muted/30 rounded-lg p-8">
                {account.crm_type === 'organization' ? (
                  <Building2 className="w-24 h-24 text-muted-foreground" />
                ) : (
                  <User className="w-24 h-24 text-muted-foreground" />
                )}
              </div>
            )}
          </div>
          {canEditPhoto && (
            <div className="px-6 pb-4 pt-2 flex flex-wrap gap-2 shrink-0">
              {(profilePhotoDataUrl || account?.main_photo_url) && (
                <Button variant="outline" size="sm" onClick={handleRotateAndSave} disabled={savingRotation}>
                  {savingRotation ? <Loader2Icon className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
                  Rotate
                </Button>
              )}
              <input type="file" id="profile-photo-upload" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('profile-photo-upload').click()} disabled={uploadingPhoto}>
                <Upload className="w-4 h-4 mr-1" /> {uploadingPhoto ? 'Uploading...' : 'Upload'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCameraOpen(true)} disabled={uploadingPhoto} data-testid="profile-take-photo-btn">
                <Camera className="w-4 h-4 mr-1" /> Camera
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CameraCaptureDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={handleCameraCapture}
      />

      {/* Share Access Key Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={(v) => { setShareDialogOpen(v); if (!v) { setShareEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Access Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Send the Owner Portal access key for <strong>{account?.system_name}</strong> via email.</p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient Email</label>
              <Input type="email" placeholder="owner@example.com" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-white"
              disabled={shareSending || !shareEmail.trim()}
              onClick={async () => {
                setShareSending(true);
                try {
                  await api.post('/crm-entities/share-access-key', {
                    entity_id: id,
                    recipient_email: shareEmail.trim(),
                  });
                  toast.success('Access key email sent');
                  setShareDialogOpen(false);
                  setShareEmail('');
                } catch { toast.error('Failed to send email'); }
                setShareSending(false);
              }}
            >
              {shareSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversion Dialog */}
      <Dialog open={conversionDialogOpen} onOpenChange={setConversionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to {convertingTo === 'account' ? 'Account' : 'Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <p className="font-medium text-yellow-900 mb-1">⚠️ Important</p>
              <p className="text-yellow-800">
                This will change the entity classification. {convertingTo === 'account' ? 'You will need to assign an Account Manager to proceed.' : 'Account management fields will no longer be required.'}
              </p>
            </div>
            
            {convertingTo === 'account' && (
              <>
                <div className="space-y-2">
                  <Label>Account Manager *</Label>
                  <Select 
                    value={conversionForm.account_manager_id}
                    onValueChange={(val) => setConversionForm({...conversionForm, account_manager_id: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Account Manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name} ${user.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Account Admin (optional)</Label>
                  <Select 
                    value={conversionForm.account_admin_id || 'none'}
                    onValueChange={(val) => setConversionForm({...conversionForm, account_admin_id: val === 'none' ? '' : val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Account Admin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name} ${user.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="account-active"
                    checked={conversionForm.account_active}
                    onChange={(e) => setConversionForm({...conversionForm, account_active: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="account-active" className="cursor-pointer">
                    Account is Active
                  </Label>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConversionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConversion}
              disabled={convertingTo === 'account' && !conversionForm.account_manager_id}
            >
              Convert to {convertingTo === 'account' ? 'Account' : 'Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default AccountDetailPage;
